import type {
  ApplyExtractionsRequest,
  AssetSummary,
  CreateCustomerRequest,
  Customer,
  CustomerAttributeInput,
  CustomerDetail,
  CustomerDocument,
  CustomerDocumentDetail,
  CustomerFieldSuggestions,
  CustomerTemplate,
  ExtractionResult,
  UpdateCustomerRequest,
  UpsertTemplateAttributesRequest,
} from '@protoss/shared';
import { apiFetch, getAuthToken } from '../../../lib/apiClient';

async function apiFetchBlob(path: string, options?: RequestInit): Promise<Blob> {
  const idToken = await getAuthToken();
  const headers = new Headers(options?.headers);
  headers.set('Authorization', `Bearer ${idToken}`);

  const response = await fetch(path, { ...options, headers });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${response.status}`);
  }

  return response.blob();
}

function getBasePath(projectUuid?: string): string {
  return projectUuid ? `/api/project/${projectUuid}/customers` : '/api/product/customers';
}

function hydrateAssetSummary(asset: AssetSummary): AssetSummary {
  return {
    ...asset,
    createdAt: new Date(asset.createdAt),
    updatedAt: new Date(asset.updatedAt),
  };
}

function hydrateCustomer<T extends Customer>(customer: T): T {
  return {
    ...customer,
    primaryImageAsset: customer.primaryImageAsset
      ? hydrateAssetSummary(customer.primaryImageAsset)
      : null,
    createdAt: new Date(customer.createdAt),
    updatedAt: new Date(customer.updatedAt),
  };
}

function hydrateCustomerDetail(customer: CustomerDetail): CustomerDetail {
  return {
    ...hydrateCustomer(customer),
    attributes: [...customer.attributes].sort(
      (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name)
    ),
  };
}

export type CustomerRecord = Customer;
export type CustomerDetailRecord = CustomerDetail;
export type CustomerTemplateRecord = CustomerTemplate;
export type CustomerFormPayload = CreateCustomerRequest;
export type CustomerUpdatePayload = UpdateCustomerRequest;
export type DynamicCustomerAttributeInput = CustomerAttributeInput;

export const customersApi = {
  async listCustomers(
    projectUuid?: string,
    options?: { query?: string }
  ): Promise<{ customers: CustomerRecord[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.query?.trim()) {
      params.set('q', options.query.trim());
    }
    const suffix = params.size > 0 ? `?${params.toString()}` : '';
    const data = await apiFetch<{ customers: Customer[]; total: number }>(
      `${getBasePath(projectUuid)}${suffix}`
    );
    return {
      customers: data.customers.map((customer) => hydrateCustomer(customer)),
      total: data.total,
    };
  },

  async getCustomer(
    projectUuid: string | undefined,
    customerUuid: string
  ): Promise<{ customer: CustomerDetailRecord }> {
    const data = await apiFetch<{ customer: CustomerDetail }>(
      `${getBasePath(projectUuid)}/${customerUuid}`
    );
    return { customer: hydrateCustomerDetail(data.customer) };
  },

  async createCustomer(
    projectUuid: string | undefined,
    data: CustomerFormPayload
  ): Promise<{ customer: CustomerRecord }> {
    return await apiFetch(`${getBasePath(projectUuid)}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateCustomer(
    projectUuid: string | undefined,
    customerUuid: string,
    data: CustomerUpdatePayload
  ): Promise<{ customer: CustomerDetailRecord }> {
    const raw = await apiFetch<{ customer: CustomerDetail }>(
      `${getBasePath(projectUuid)}/${customerUuid}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
    return { customer: hydrateCustomerDetail(raw.customer) };
  },

  async uploadCustomerImage(
    projectUuid: string | undefined,
    customerUuid: string,
    file: File
  ): Promise<{ asset: AssetSummary; customer?: CustomerRecord | null }> {
    const formData = new FormData();
    formData.set('file', file);

    return await apiFetch(`${getBasePath(projectUuid)}/${customerUuid}/image`, {
      method: 'POST',
      body: formData,
    });
  },

  async removeCustomerImage(projectUuid: string | undefined, customerUuid: string): Promise<void> {
    return await apiFetch(`${getBasePath(projectUuid)}/${customerUuid}/image`, {
      method: 'DELETE',
    });
  },

  async deleteCustomer(projectUuid: string | undefined, customerUuid: string): Promise<void> {
    return await apiFetch(`${getBasePath(projectUuid)}/${customerUuid}`, {
      method: 'DELETE',
    });
  },

  async deleteCustomerAttributeValue(
    projectUuid: string | undefined,
    customerUuid: string,
    fieldKey: string
  ): Promise<{ customer: CustomerDetailRecord }> {
    const raw = await apiFetch<{ customer: CustomerDetail }>(
      `${getBasePath(projectUuid)}/${customerUuid}/attributes/${encodeURIComponent(fieldKey)}`,
      { method: 'DELETE' }
    );
    return { customer: hydrateCustomerDetail(raw.customer) };
  },

  async confirmAttributeValue(
    projectUuid: string | undefined,
    customerUuid: string,
    fieldKey: string
  ): Promise<void> {
    return await apiFetch(
      `${getBasePath(projectUuid)}/${customerUuid}/attributes/${encodeURIComponent(fieldKey)}/confirm`,
      { method: 'POST' }
    );
  },

  async rejectAttributeValue(
    projectUuid: string | undefined,
    customerUuid: string,
    fieldKey: string
  ): Promise<void> {
    return await apiFetch(
      `${getBasePath(projectUuid)}/${customerUuid}/attributes/${encodeURIComponent(fieldKey)}/reject`,
      { method: 'POST' }
    );
  },

  async getTemplate(projectUuid?: string): Promise<{ template: CustomerTemplateRecord }> {
    return await apiFetch(`${getBasePath(projectUuid)}/template`);
  },

  async getFieldSuggestions(
    projectUuid?: string
  ): Promise<{ suggestions: CustomerFieldSuggestions }> {
    return await apiFetch(`${getBasePath(projectUuid)}/suggestions`);
  },

  async getFieldValuesBySource(
    source: string,
    projectUuid?: string
  ): Promise<{ values: string[] }> {
    const params = new URLSearchParams({ source });
    return await apiFetch(`${getBasePath(projectUuid)}/field-values?${params.toString()}`);
  },

  async checkEmailExists(
    email: string,
    projectUuid?: string,
    excludeUuid?: string
  ): Promise<{ exists: boolean; customerUuid: string | null; customerName: string | null }> {
    const params = new URLSearchParams({ email });
    if (excludeUuid) params.set('excludeUuid', excludeUuid);
    return await apiFetch(`${getBasePath(projectUuid)}/check-email?${params.toString()}`);
  },

  async checkLinkedinExists(
    linkedin: string,
    projectUuid?: string,
    excludeUuid?: string
  ): Promise<{ exists: boolean; customerUuid: string | null; customerName: string | null }> {
    const params = new URLSearchParams({ linkedin });
    if (excludeUuid) params.set('excludeUuid', excludeUuid);
    return await apiFetch(`${getBasePath(projectUuid)}/check-linkedin?${params.toString()}`);
  },

  async updateTemplate(
    projectUuid: string | undefined,
    data: UpsertTemplateAttributesRequest
  ): Promise<{ template: CustomerTemplateRecord }> {
    return await apiFetch(`${getBasePath(projectUuid)}/template`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteTemplateAttribute(fieldKey: string, projectUuid?: string): Promise<void> {
    return await apiFetch(`${getBasePath(projectUuid)}/template/${encodeURIComponent(fieldKey)}`, {
      method: 'DELETE',
    });
  },

  async getProtectedAssetObjectUrl(
    path: string,
    options?: { signal?: AbortSignal }
  ): Promise<string> {
    const blob = await apiFetchBlob(path, { signal: options?.signal });
    return URL.createObjectURL(blob);
  },

  async listDocuments(
    customerUuid: string
  ): Promise<{ documents: CustomerDocument[]; total: number }> {
    return apiFetch(`/api/product/customers/${customerUuid}/documents`);
  },

  async getDocument(
    customerUuid: string,
    docUuid: string
  ): Promise<{ document: CustomerDocumentDetail }> {
    return apiFetch(`/api/product/customers/${customerUuid}/documents/${docUuid}`);
  },

  async createPasteDocument(
    customerUuid: string,
    text: string,
    title?: string
  ): Promise<{ document: CustomerDocument }> {
    return apiFetch(`/api/product/customers/${customerUuid}/documents`, {
      method: 'POST',
      body: JSON.stringify({ text, title }),
    });
  },

  async createUploadDocument(
    customerUuid: string,
    file: File,
    title?: string
  ): Promise<{ document: CustomerDocument }> {
    const formData = new FormData();
    formData.set('file', file);
    if (title) formData.set('title', title);
    return apiFetch(`/api/product/customers/${customerUuid}/documents`, {
      method: 'POST',
      body: formData,
    });
  },

  async updatePasteDocument(
    customerUuid: string,
    docUuid: string,
    text: string,
    title?: string
  ): Promise<{ document: CustomerDocumentDetail }> {
    return apiFetch(`/api/product/customers/${customerUuid}/documents/${docUuid}`, {
      method: 'PATCH',
      body: JSON.stringify({ text, title }),
    });
  },

  async deleteDocument(customerUuid: string, docUuid: string): Promise<void> {
    return apiFetch(`/api/product/customers/${customerUuid}/documents/${docUuid}`, {
      method: 'DELETE',
    });
  },

  async startExtraction(customerUuid: string, docUuid: string): Promise<{ runId: number }> {
    return apiFetch(`/api/product/customers/${customerUuid}/documents/${docUuid}/extract`, {
      method: 'POST',
    });
  },

  async pollExtractionRun(runId: number): Promise<{
    status: 'pending' | 'running' | 'completed' | 'failed';
    extractions?: ExtractionResult[];
    error?: string;
  }> {
    return apiFetch(`/api/product/customers/extract-runs/${runId}`);
  },

  async applyExtractions(
    customerUuid: string,
    body: ApplyExtractionsRequest
  ): Promise<{ jobId: number }> {
    return apiFetch(`/api/product/customers/${customerUuid}/apply-extractions`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async pollApplyRun(jobId: number): Promise<{ status: string }> {
    return apiFetch(`/api/product/customers/apply-runs/${jobId}`);
  },

  async startImportArchon(text: string): Promise<{ runId: number }> {
    return apiFetch('/api/product/customers/import/archon', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  },

  async startImportCsv(file: File, precomputedMappings?: unknown[]): Promise<{ runId: number }> {
    const formData = new FormData();
    formData.set('file', file);
    if (precomputedMappings && precomputedMappings.length > 0) {
      formData.set('mappings', JSON.stringify(precomputedMappings));
    }
    return apiFetch('/api/product/customers/import/csv', {
      method: 'POST',
      body: formData,
    });
  },

  async pollCsvImportRun(runId: number): Promise<{
    status: 'pending' | 'running' | 'completed' | 'failed';
    result?: {
      filename: string;
      rowCount: number;
      imported: number;
      duplicate: number;
      failed: number;
    };
    error?: string;
  }> {
    return apiFetch(`/api/product/customers/import/csv-runs/${runId}`);
  },

  async startCsvPreview(file: File): Promise<{ runId: number }> {
    const formData = new FormData();
    formData.set('file', file);
    return apiFetch('/api/product/customers/import/csv/preview', {
      method: 'POST',
      body: formData,
    });
  },

  async pollCsvPreviewRun(runId: number): Promise<{
    status: 'pending' | 'running' | 'completed' | 'failed';
    preview?: {
      filename: string;
      totalRows: number;
      newRows: number;
      duplicateRows: number;
      noContactRows: number;
      mappedColumns: number;
      mappings: Array<{
        csvColumn: string;
        fieldKey: string;
        fieldLabel: string;
      }>;
      /** Raw AI output - passed back to the import job to skip re-inference */
      rawMappings: unknown[];
      preview: Array<{
        name: string;
        email: string | null;
        phone: string | null;
        linkedin: string | null;
        status: 'new' | 'duplicate' | 'no_contact';
        matchedBy: 'email' | 'linkedin' | null;
        extra: Array<{ fieldKey: string; label: string; value: string }>;
      }>;
    };
    error?: string;
  }> {
    return apiFetch(`/api/product/customers/import/csv-preview-runs/${runId}`);
  },
};
